import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi, useBalloonEventsMetadata, useUser } from 'react-ui';
import { BeatAPITagRepository, TagApiError } from 'ts-core';
import type { Tag } from 'ts-core';

interface TagSelectorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  className?: string;
  placeholder?: string;
  showSystemTags?: boolean;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select tags...',
  showSystemTags = false,
}) => {
  const { metadata, loading, refreshMetadata } = useBalloonEventsMetadata();
  const { apiEndpoints, apiKey } = useApi();
  const { user } = useUser();
  const token = user?.token ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [localTags, setLocalTags] = useState<Tag[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tagRepo = useMemo(
    () => new BeatAPITagRepository(apiEndpoints.tags, apiKey ?? ''),
    [apiEndpoints.tags, apiKey]
  );

  const availableTags = useMemo((): Tag[] => {
    const metaTags: Tag[] = (metadata?.tags ?? []).filter(
      (tag: Tag) => showSystemTags || tag.type !== 'system'
    );
    const localSlugs = new Set(localTags.map((t) => t.slug));
    return [...metaTags.filter((t) => !localSlugs.has(t.slug)), ...localTags];
  }, [metadata, localTags, showSystemTags]);

  const visibleTags = useMemo((): Tag[] => {
    return availableTags;
  }, [availableTags]);

  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return visibleTags;
    const query = searchQuery.toLowerCase();
    return visibleTags.filter((tag: Tag) =>
      tag.name.toLowerCase().includes(query) ||
      tag.slug.toLowerCase().includes(query)
    );
  }, [visibleTags, searchQuery]);

  const selectedTags = useMemo(() => {
    return visibleTags.filter((tag: Tag) => value.includes(tag.slug));
  }, [visibleTags, value]);

  const trimmedQuery = searchQuery.trim();
  const hasExactMatch = trimmedQuery
    ? visibleTags.some((t) => t.name.toLowerCase() === trimmedQuery.toLowerCase())
    : false;
  const canCreate = !!trimmedQuery && !hasExactMatch && !!token;
  const showSignInHint = !!trimmedQuery && !hasExactMatch && !token;

  const showError = useCallback((message: string) => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setCreateError(message);
    errorTimerRef.current = setTimeout(() => setCreateError(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const metadataTagSlugs = new Set((metadata?.tags ?? []).map((tag: Tag) => tag.slug));

    if (metadataTagSlugs.size === 0) {
      return;
    }

    setLocalTags((prev) => prev.filter((tag) => !metadataTagSlugs.has(tag.slug)));
  }, [metadata]);

  const handleTagToggle = (tagSlug: string) => {
    if (value.includes(tagSlug)) {
      onChange(value.filter((t) => t !== tagSlug));
    } else {
      onChange([...value, tagSlug]);
    }
    setSearchQuery('');
  };

  const handleRemoveTag = (tagSlug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((t) => t !== tagSlug));
  };

  const handleCreateTag = useCallback(async (name: string) => {
    if (!token || isCreating) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      const newTag = await tagRepo.createTag(name, token);
      const newTagEntry: Tag = { id: newTag.id, name: newTag.name, slug: newTag.slug, type: 'user' };
      setLocalTags((prev) => [...prev, newTagEntry]);
      if (!value.includes(newTag.slug)) {
        onChange([...value, newTag.slug]);
      }
      setSearchQuery('');
      setIsOpen(false);
      await refreshMetadata();
    } catch (err) {
      if (err instanceof TagApiError && err.status === 409) {
        const existingSlug =
          'details' in err &&
          err.details &&
          'existing_tag' in err.details &&
          err.details.existing_tag &&
          'slug' in err.details.existing_tag
            ? err.details.existing_tag.slug
            : visibleTags.find((t) => t.name.toLowerCase() === name.toLowerCase())?.slug;

        if (existingSlug && !value.includes(existingSlug)) {
          onChange([...value, existingSlug]);
          setSearchQuery('');
        }
        setIsOpen(false);
        await refreshMetadata();
      } else if (err instanceof TagApiError && err.status === 422) {
        showError('Tag contains inappropriate content');
      } else {
        showError('Failed to create tag. Please try again.');
      }
    } finally {
      setIsCreating(false);
    }
  }, [token, isCreating, tagRepo, value, onChange, refreshMetadata, visibleTags, showError]);

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && trimmedQuery) {
      e.preventDefault();
      const exactMatch = visibleTags.find(
        (t) => t.name.toLowerCase() === trimmedQuery.toLowerCase()
      );
      if (exactMatch) {
        handleTagToggle(exactMatch.slug);
      } else if (token) {
        void handleCreateTag(trimmedQuery);
      }
      return;
    }
    if (e.key === 'Backspace' && !searchQuery && value.length > 0) {
      onChange(value.slice(0, -1));
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-10 bg-gray-200 rounded-md" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input with pills */}
      <div
        className={`
          flex flex-wrap items-center gap-1.5 min-h-[42px] px-3 py-2
          border rounded-md bg-white cursor-text
          ${isOpen ? 'border-balloon ring-2 ring-balloon/20' : 'border-gray-300'}
        `}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Selected tag pills */}
        {selectedTags.map((tag: Tag) => (
          <span
            key={tag.slug}
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-sm rounded-md ${
              tag.type === 'system'
                ? 'bg-gray-100 text-gray-600 border border-gray-300'
                : 'bg-balloon text-white'
            }`}
          >
            <span>{tag.name}</span>
            {tag.type === 'system' && (
              <span className="text-xs leading-none" title="System tag" aria-label="System tag">
                ⚙
              </span>
            )}
            <button
              type="button"
              onClick={(e) => handleRemoveTag(tag.slug, e)}
              className={`rounded-sm p-0.5 -mr-1 ${
                tag.type === 'system' ? 'hover:bg-gray-200' : 'hover:bg-white/20'
              }`}
              aria-label={`Remove ${tag.name}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          disabled={isCreating}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] outline-none text-sm bg-transparent disabled:opacity-50"
          aria-label="Search or create tags"
        />
      </div>

      {/* Inline error */}
      {createError && (
        <p className="mt-1 text-xs text-red-500" role="alert">{createError}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {/* Search hint */}
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search tags...
            </div>
          </div>

          {/* Creating state */}
          {isCreating && (
            <div className="px-3 py-3 text-sm text-gray-500 flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin text-balloon" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Creating "{trimmedQuery}"…
            </div>
          )}

          {/* Tag list */}
          {!isCreating && (
            <>
              {filteredTags.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  No tags found
                </div>
              ) : (
                <div className="py-1">
                  {filteredTags.map((tag: Tag) => {
                    const isSelected = value.includes(tag.slug);
                    const isSystemTag = tag.type === 'system';
                    return (
                      <button
                        key={tag.slug}
                        type="button"
                        onClick={() => handleTagToggle(tag.slug)}
                        className={`
                          w-full flex items-center justify-between px-3 py-2 text-sm text-left
                          hover:bg-gray-50 transition-colors
                          ${isSelected ? 'bg-gray-50' : ''}
                        `}
                      >
                        <span className={`inline-flex items-center gap-1 ${isSelected ? 'font-medium' : ''} ${isSystemTag ? 'text-gray-600' : ''}`}>
                          <span>{tag.name}</span>
                          {isSystemTag && (
                            <span className="text-xs leading-none text-gray-400" title="System tag" aria-label="System tag">
                              ⚙
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <svg className={`w-4 h-4 ${isSystemTag ? 'text-gray-500' : 'text-balloon'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Create prompt */}
              {canCreate && (
                <button
                  type="button"
                  onClick={() => void handleCreateTag(trimmedQuery)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-balloon border-t border-gray-100 hover:bg-balloon/5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create "{trimmedQuery}"
                </button>
              )}

              {/* Sign-in hint */}
              {showSignInHint && (
                <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100 text-center">
                  Sign in to create new tags
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
